import request from "../../utils/request";
import { Result } from "../../models/general";

/**
 * CO-830 — one row of the Action Trail (server: ActionTrailController view model). Admin and HR.
 *
 * Redaction happens on the SERVER: for an Admin reading an EmployeeData row whose subject is not the caller,
 * `path`, `targetId`, `targetName` and `changes` arrive as null and `redacted` is true. HR gets
 * every row unredacted. Personal/financial values inside `changes` arrive blank with `hidden: true` for anyone
 * who is not HR. The client never receives what is hidden, so nothing here can reveal it.
 */
export type ActionTrailKind = "Other" | "Update" | "Create" | "Delete";
export type ActionTrailOutcome = "Success" | "Denied" | "Failed";
/** CO-953: no user / a user of the caller's company / a platform user (Mowazaf staff) / an id that no longer resolves. */
export type ActionTrailPersonKind = "System" | "Person" | "Support" | "FormerEmployee";

export interface ActionTrailEvent {
  id: number;
  occurredUtc: string;
  /** Only resolved for users inside the caller's company; null otherwise. */
  actorName?: string | null;
  /** Staff number, shown when the name is missing. */
  actorStaffNo?: string | null;
  /**
   * CO-953 (3): the user ids no longer reach the client (they are system keys). The server says what kind of
   * person it is instead. Absent only on an API older than this field.
   */
  actorKind?: ActionTrailPersonKind;
  actorRoles?: string | null;
  /** Controller name without the "Controller" suffix, e.g. "Match". */
  area: string;
  /** MVC action name, e.g. "UpdateMatch". */
  action: string;
  kind: ActionTrailKind | string;
  kindCode: number;
  outcome: ActionTrailOutcome | string;
  outcomeCode: number;
  statusCode: number;
  httpMethod?: string | null;
  routeTemplate?: string | null;
  /** Null when redacted. */
  path?: string | null;
  targetType?: string | null;
  /** Null when redacted. */
  targetId?: string | null;
  /** Null when redacted. */
  targetName?: string | null;
  /** Null when redacted. Staff number, shown when the name is missing. */
  targetStaffNo?: string | null;
  /** `System` when there is no subject user or the row is redacted. */
  targetKind?: ActionTrailPersonKind;
  ip?: string | null;
  userAgent?: string | null;
  correlationId?: string | null;
  durationMs: number;
  redacted: boolean;
  /** Records the action wrote, with before/after values. Absent on redacted rows and rows older than 2026-10-06. */
  changes?: ActionTrailChange[] | null;
  /** Records written beyond the capture cap (counted, not kept). */
  omittedChanges?: number;
}

export type ActionTrailChangeState = "Added" | "Modified" | "Deleted";

export interface ActionTrailChange {
  /** Entity name, e.g. "LeavePolicy" (the client humanises it). */
  entity: string;
  key?: string | null;
  state: ActionTrailChangeState | string;
  fields: ActionTrailFieldChange[];
  omittedFields?: number;
}

export interface ActionTrailFieldChange {
  /** Property name, e.g. "MaxDays" (the client humanises it). */
  name: string;
  before?: string | null;
  after?: string | null;
  /** The value exists but this reader may not see it (personal/financial data, reader is not HR). */
  hidden?: boolean;
  /** A credential: only the fact that it changed was recorded. */
  secret?: boolean;
}

export interface ActionTrailPage {
  items: ActionTrailEvent[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface ActionTrailArea {
  area: string;
  sensitivity: "General" | "EmployeeData" | string;
}

/** Kind codes on the wire (server enum). */
export const ACTION_TRAIL_KIND_CODES: Record<ActionTrailKind, number> = { Other: 0, Create: 1, Update: 2, Delete: 3 };
/** Outcome codes on the wire (server enum). */
export const ACTION_TRAIL_OUTCOME_CODES: Record<ActionTrailOutcome, number> = { Success: 0, Denied: 1, Failed: 2 };

/**
 * Query of GET api/action-trail. Dates are yyyy-MM-dd (UTC, inclusive). `actor` / `targetUser` accept a
 * user id or an in-company email; `targetUser` only matches rows whose subject the caller may see.
 */
export interface ActionTrailQuery {
  from?: string;
  to?: string;
  area?: string;
  kind?: number;
  outcome?: number;
  actor?: string;
  targetUser?: string;
  pageNumber: number;
  pageSize: number;
}

function clean(query: ActionTrailQuery): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.entries(query).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") out[k] = v as string | number;
  });
  return out;
}

/** Admin and HR, own company only (server-enforced). The read itself is audited (ActionTrailViewed). */
export async function getActionTrailEvents(query: ActionTrailQuery) {
  const response = await request.get<Result<ActionTrailPage>>("action-trail", { params: clean(query) });
  return response.data;
}

/** Distinct areas recorded for the caller's company, with their sensitivity (drives the Area filter). */
export async function getActionTrailAreas() {
  const response = await request.get<Result<ActionTrailArea[]>>("action-trail/areas");
  return response.data;
}

/** CSV of exactly the page described by `query`, redacted like the JSON. Audited (ActionTrailExported). */
export async function exportActionTrailPage(query: ActionTrailQuery): Promise<Blob> {
  const response = await request.get("action-trail/export", { params: clean(query), responseType: "blob" });
  return response.data as Blob;
}
